import * as React from 'react';

import type {UserCreateInput} from 'nutshell-graphql-types';

import {useGetUsersByFilter, useCreateUser} from '../graphql/hooks';

import {CreateUserModal} from './create-user-modal';
import {
    getTableTitleFromStatus,
    getEmptyStateMessageFromStatus,
    getDisableAddingUserFromStatus,
} from './helpers';
import {UsersTable} from './users-table';

type Props = {
    statusFilter: string;
};

export function StatusTableWrapper(props: Props) {
    const [isCreatingUser, setIsCreatingUser] = React.useState(false);
    const [createUserError, setCreateUserError] = React.useState<any>(null);

    const {
        users,
        isLoading,
        errorMessage,
        fetchMoreRows,
        hasNextPage,
        totalCount,
        endCursor,
        refetch: refetchUsers,
    } = useGetUsersByFilter({statusFilter: parseInt(props.statusFilter)});
    const {createUser, isCreating, error, existingUserId} = useCreateUser();

    const onCreateUser = (input: UserCreateInput) => {
        setCreateUserError(null);
        createUser(input).then((result) => {
            if (
                result &&
                result.data &&
                result.data.userCreate &&
                result.data.userCreate.isExistingUser
            ) {
                return;
            }

            fetchMoreRows();

            // Timeout allows the success animation to play before closing the modal
            setTimeout(() => {
                setIsCreatingUser(false);
                window.location.reload();
            }, 1000);
        });
    };

    React.useEffect(() => {
        if (error) {
            setCreateUserError(error);
        }
    }, [error]);

    // Refetch users when the table loads after its first load so we show any new users that were added
    React.useEffect(() => {
        if (endCursor) {
            fetchMoreRows();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.statusFilter]);

    const disableAddingUser = getDisableAddingUserFromStatus(props.statusFilter);

    return (
        <>
            <UsersTable
                title={getTableTitleFromStatus(props.statusFilter)}
                users={users}
                isLoading={isLoading}
                errorMessage={errorMessage}
                fetchMore={fetchMoreRows}
                hasNextPage={hasNextPage}
                totalCount={totalCount}
                emptyStateMessage={getEmptyStateMessageFromStatus(props.statusFilter)}
                onAddRowClick={disableAddingUser ? undefined : () => setIsCreatingUser(true)}
                refetchUsers={refetchUsers}
            />
            {isCreatingUser && (
                <CreateUserModal
                    onSubmit={onCreateUser}
                    onClose={() => {
                        setIsCreatingUser(false);
                        setCreateUserError(null);
                    }}
                    isSaving={isCreating}
                    errorMessage={createUserError}
                    existingUserId={existingUserId}
                />
            )}
        </>
    );
}
